import { ColumnDef } from '@tanstack/react-table';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import DrawerWrapper, { DrawerFooter } from 'components/IAP/DrawerWrapper';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useCallback, useMemo } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';

type FormFields = {
  equipmentTypes: {
    type: string;
  }[];
};

export type EditEquipmentTypesDrawerProps = {
  toggle: () => void;
  equipmentTypes: string[];
  onSave: (equipmentTypes: Set<string>) => void;
};

const AssignedResourcesEquipmentTypesDrawer: React.FC<
  EditEquipmentTypesDrawerProps
> = ({ toggle, equipmentTypes, onSave }) => {
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      equipmentTypes: equipmentTypes
        ? equipmentTypes.map((type) => ({ type }))
        : [],
    },
  });

  const { handleSubmit, register, control, watch } = form;
  const watchedFields = watch();

  const { fields, remove, append } = useFieldArray({
    control,
    name: 'equipmentTypes',
  });

  const submitEquipmentTypesEdit = async (formData: FormFields) => {
    const updatedEquipmentTypes = new Set<string>(
      formData.equipmentTypes.map((et) => et.type)
    );

    onSave(updatedEquipmentTypes);
    toggle();
  };

  const appendBlankEquipmentType = useCallback(() => {
    append({ type: '' });
  }, [append]);

  const deleteRow = useCallback(
    (rowToDelete: { id: string; type: string }) => {
      const index = fields.findIndex((field) => field.id === rowToDelete.id);
      if (index !== -1) {
        remove(index);
      }
    },
    [remove, fields]
  );

  const saveDisabled = Object.values(watchedFields.equipmentTypes).some(
    (et) => !et.type.trim()
  );

  const columns = useMemo<ColumnDef<{ id: string; type: string }>[]>(
    () => [
      {
        accessorKey: 'type',
        header: 'Equipment Type',
        cell: (info: any) => (
          <input
            type="text"
            className="form-control"
            {...register(`equipmentTypes.${info.row.index}.type`)}
          />
        ),
        meta: { headerAlign: 'left' },
      },
      {
        id: 'actions',
        header: () => <span />,
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <StylishNewButton
              className="button--secondary border-0"
              onClick={(e) => {
                e.stopPropagation();
                deleteRow(row.original);
              }}
            >
              <SharedIcon iconName="delete" classes="me-1" />
            </StylishNewButton>
          </div>
        ),
        enableSorting: false,
        size: 160,
        meta: { headerAlign: 'left' },
      },
    ],
    [deleteRow, register]
  );

  return (
    <DrawerWrapper toggle={toggle} title="Edit Equipment Types">
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(submitEquipmentTypesEdit)}>
          <Offcanvas.Body>
            <PratusTable
              className="layout-auto pratus-table"
              columns={columns}
              data={fields}
              getRowId={(row) => row.id}
              noDataIndication={
                <div className="text-center p-2">
                  {
                    'There are no equipment types yet. Click + Add Equipment Type to add an equipment type.'
                  }
                </div>
              }
              footer={
                <div style={{ paddingLeft: '4rem' }}>
                  <StylishNewButton
                    className="button button--primary button--sml"
                    onClick={(e) => {
                      e.stopPropagation();
                      appendBlankEquipmentType();
                    }}
                  >
                    <SharedIcon iconName="add" classes="me-2" />
                    Add Equipment Type
                  </StylishNewButton>
                </div>
              }
            />
          </Offcanvas.Body>
        </form>
      </FormProvider>
      <DrawerFooter>
        <StylishNewButton
          className="button--secondary button--reverse"
          type="button"
          onClick={toggle}
        >
          Close
        </StylishNewButton>
        <StylishNewButton
          className="button--primary"
          type="submit"
          disabled={saveDisabled}
          onClick={(e) => {
            e.stopPropagation();
            submitEquipmentTypesEdit(watchedFields);
            toggle();
          }}
        >
          Save
        </StylishNewButton>
      </DrawerFooter>
    </DrawerWrapper>
  );
};

export default AssignedResourcesEquipmentTypesDrawer;
